import { Card, CardContent, Snackbar, Button, Alert } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import viewsTexts from "../../../common/viewsTexts.json";
import {
  BreadCrumbs,
  CardHeaderWidget,
  SaveAction,
  TextFieldWidgetDateRange,
} from "../../../components";
import { AxiosContext } from "../../../context/AxiosContext";
import { Context } from "../../../context/Context";
import { alphabeticalSort } from "../../../helpers";
import { BsCol, BsContainer, BsRow } from "../../../layouts/components";
// import theme from 'theme';
import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";
import { Checkbox, FormControlLabel } from "@mui/material";
import TextFieldWidgetText2 from "../../../components/TextFieldWidget/TextFieldWidgetText2/TextFieldWidgetText2";
import {
  formatDateToMMDDYYYY,
  formatRutWithoutDots,
} from "helpers/functionHelpers";

const PhAdd = (props) => {
  const axiosContext = useContext(AxiosContext);

  const endpointName = "v1/libraryExternal/panel-admin-create";
  const endpointNamePost = "v1/libraryExternal";
  const { userId } = useContext(Context);
  const [loadingQueryState, setLoadingQueryState] = useState(false);
  const [successQueryState, setSuccessQueryState] = useState(false);
  const [dataState, setDataState] = useState([]);
  const [loadingDataState, setLoadingDataState] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });
  const [clientOptions, setClientOptions] = useState([]); //* Opcional por si se requiere implementar select de cliente
  const [productorIdOptions, setProductorIdOptions] = useState([]);
  const [agroBusinessOptions, setAgroBusinessOptions] = useState([]); //* Opciones de Agrícola
  const [filteredGroupOptions, setFilteredGroupOptions] = useState([]); //* Opciones de Grupo filtradas
  const [orchardOptions, setOrchardOptions] = useState([]);
  const [filteredOrchardOptions, setFilteredOrchardOptions] = useState([]); //* Opciones de Huerto filtradas por Grupo
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [technicBossOptions, setTechnicBossOptions] = useState([]);
  const [allCsgOptions, setAllCsgOptions] = useState([]);
  const [filteredCsgPrestadoOptions, setFilteredCsgPrestadoOptions] = useState(
    []
  ); //* CSG Asociado filtrado sin los del huerto seleccionado
  const [filteredOrchardCsgOptions, setFilteredOrchardCsgOptions] = useState(
    []
  ); //* CSGs del huerto seleccionado
  const [filteredVarietyOptions, setFilteredVarietyOptions] = useState([]);
  const [filteredProductLineOptions, setFilteredProductLineOptions] = useState(
    []
  );
  const [showNewValueInputs, setShowNewValueInputs] = useState({
    productor: false,
    csg: false,
    orchard: false,
  });
  const [csgValidationState, setCsgValidationState] = useState({
    isValidating: false,
    foundMatch: false,
    matchedCsg: null,
    isNewCsg: false,
  });
  const [csgDebounceTimeout, setCsgDebounceTimeout] = useState(null);
  const [productorValidationState, setProductorValidationState] = useState({
    isValidating: false,
    foundMatch: false,
    matchedProductor: null,
    isNewProductor: false,
  });
  const [productorDebounceTimeout, setProductorDebounceTimeout] =
    useState(null);
  const [isUpdatingProductorAgroBusiness, setIsUpdatingProductorAgroBusiness] =
    useState(false);
  // Visibilidad de la línea nueva. Si no se ve en planificación, el Pactado y
  // las fechas son opcionales y el backend no crea filas en PlanningHarvest.
  const [showPlanningHarvestState, setShowPlanningHarvestState] =
    useState(true);
  const [showTechnicalReportState, setShowTechnicalReportState] =
    useState(true);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();

  const watchSpecie = watch("specie");
  const watchProductor = watch("productor");
  const watchAgroBusiness = watch("agroBusiness");
  const watchOrchard = watch("orchard");
  const watchVariety = watch("variety");
  const watchGroup = watch("group");
  const watchCsg_prestado = watch("csg_prestado");
  const watchAgroBusiness_custom = watch("agroBusiness_custom");
  const watchGroup_custom = watch("group_custom");
  const watchProductor_custom = watch("productor_custom");
  const watchCsg = watch("csg");
  const watchOrchardCustom = watch("orchard_custom");

  const clearSelects = (setValue, fields) => {
    fields.forEach((f) => setValue(f, null));
  };

  // Función helper para limpiar RUT (quitar puntos y guión)
  const cleanRutForComparison = (rut) => {
    if (!rut) return "";
    return rut.replace(/\./g, "").replace(/-/g, "").toUpperCase();
  };

  const getProductorObj = useCallback(() => {
    if (!watchProductor?.value || !dataState?.productorID) return null;
    return dataState.productorID.find(
      (item) => item.value === watchProductor.value
    );
  }, [watchProductor, dataState]);

  const getAgroBusinessObj = useCallback(() => {
    if (!watchAgroBusiness?.value || !dataState?.agroBusiness) return null;
    return dataState.agroBusiness.find(
      (item) => item.value === watchAgroBusiness.value
    );
  }, [watchAgroBusiness, dataState]);

  const getOrchardObj = useCallback(() => {
    if (!watchOrchard?.value || !dataState?.orchard) return null;
    return dataState.orchard.find((item) => item.value === watchOrchard.value);
  }, [watchOrchard, dataState]);

  const getSpecieObj = useCallback(() => {
    if (!watchSpecie?.value || !dataState?.specie) return null;
    return dataState.specie.find((item) => item.value === watchSpecie.value);
  }, [watchSpecie, dataState]);

  const getVarietyObj = useCallback(() => {
    const specieObj = getSpecieObj();
    if (!specieObj || !watchVariety?.value) return null;
    return specieObj.variety?.find((item) => item.value === watchVariety.value);
  }, [getSpecieObj, watchVariety]);

  const getGroupObj = useCallback(() => {
    if (!watchGroup?.value || !dataState?.productorID) return null;
    
    // Recolectar todos los huertos del grupo seleccionado desde la estructura anidada
    const orchardsList = [];
    
    for (const productor of dataState.productorID) {
      if (productor.agroBusiness) {
        for (const agroBusiness of productor.agroBusiness) {
          if (agroBusiness.group) {
            const foundGroup = agroBusiness.group.find(
              (group) => group.value === watchGroup.value
            );
            if (foundGroup && foundGroup.orchard) {
              // Agregar todos los huertos de este grupo
              orchardsList.push(...foundGroup.orchard);
            }
          }
        }
      }
    }
    
    // Retornar un objeto con todos los huertos únicos del grupo
    if (orchardsList.length > 0) {
      // Eliminar duplicados basándose en el valor
      const uniqueOrchards = orchardsList.filter((orchard, index, self) =>
        index === self.findIndex((o) => o.value === orchard.value)
      );
      
      return {
        value: watchGroup.value,
        label: watchGroup.label,
        orchard: uniqueOrchards
      };
    }
    
    return null;
  }, [watchGroup, dataState]);

  const handleNewValueCheckboxChange = useCallback(
    (fieldName) => (event) => {
      const isChecked = event.target.checked;
      setShowNewValueInputs((prev) => ({
        ...prev,
        [fieldName]: isChecked,
      }));

      if (fieldName === "productor") {
        if (isChecked) {
          setValue("agroBusiness_custom", "");
          setValue("group_custom", "");
          setValue("productor", null);
          setValue("agroBusiness", null);
          setValue("group", null);
          setValue("orchard", null); // Limpiar huerto también
          setProductorValidationState({
            isValidating: false,
            foundMatch: false,
            matchedProductor: null,
            isNewProductor: false,
          });
          // Mostrar todos los huertos cuando se usa nuevo productor
          setFilteredOrchardOptions(orchardOptions);
        } else {
          setValue("agroBusiness_custom", "");
          setValue("group_custom", "");
          setValue("productor_custom", "");
          setValue("orchard", null); // Limpiar huerto también
          setProductorValidationState({
            isValidating: false,
            foundMatch: false,
            matchedProductor: null,
            isNewProductor: false,
          });
          // Resetear a todas las opciones cuando se desmarca nuevo productor
          setFilteredOrchardOptions(orchardOptions);
        }
      }

      if (fieldName === "orchard") {
        if (isChecked) {
          setValue("orchard", null);
          setValue("orchard_custom", "");
          setValue("csg", "");
          setValue("csg_prestado", null);
          setShowNewValueInputs((prev) => ({
            ...prev,
            csg: true,
          }));
          setCsgValidationState({
            isValidating: false,
            foundMatch: false,
            matchedCsg: null,
            isNewCsg: false,
          });
        } else {
          setValue("orchard_custom", "");
          setShowNewValueInputs((prev) => ({
            ...prev,
            csg: false,
          }));
        }
      }

      if (fieldName === "csg") {
        if (isChecked) {
          setValue("csg", "");
          setValue("csg_prestado", null);
          setCsgValidationState({
            isValidating: false,
            foundMatch: false,
            matchedCsg: null,
            isNewCsg: false,
          });
        } else {
          setValue("csg_prestado", null);
          if (
            !showNewValueInputs.orchard &&
            watchOrchard?.value &&
            dataState?.orchard
          ) {
            const orchardObj = getOrchardObj();
            if (orchardObj?.csg && orchardObj.csg.length > 0) {
              setValue("csg", orchardObj.csg[0].value);
            }
          }
        }
      }
    },
    [
      setValue,
      watchProductor,
      watchOrchard,
      dataState,
      getProductorObj,
      getOrchardObj,
      orchardOptions,
    ]
  );

  // useEffect para productor -> agroBusiness + grupo (sincronización bidireccional)
  useEffect(() => {
    if (isUpdatingProductorAgroBusiness) return;

    if (
      watchProductor &&
      watchProductor.value &&
      dataState?.productorID &&
      !showNewValueInputs.productor
    ) {
      setIsUpdatingProductorAgroBusiness(true);

      clearErrors(["productor", "agroBusiness", "group"]);

      const productorObj = getProductorObj();
      if (productorObj?.agroBusiness && productorObj.agroBusiness.length > 0) {
        const firstAgroBusiness = productorObj.agroBusiness[0];
        setValue("agroBusiness", firstAgroBusiness);
        setValue("agroBusiness_custom", firstAgroBusiness.label);

        if (firstAgroBusiness.group && firstAgroBusiness.group.length > 0) {
          const firstGroup = firstAgroBusiness.group[0];
          setValue("group", firstGroup);
          setValue("group_custom", firstGroup.label);
        } else {
          clearSelects(setValue, ["group"]);
          setValue("group_custom", "");
        }
      } else {
        clearSelects(setValue, ["agroBusiness", "group"]);
        setValue("agroBusiness_custom", "");
        setValue("group_custom", "");
      }

      setTimeout(() => setIsUpdatingProductorAgroBusiness(false), 0);
    } else if (!showNewValueInputs.productor) {
      clearSelects(setValue, ["agroBusiness", "group"]);
      setValue("agroBusiness_custom", "");
      setValue("group_custom", "");
    }
  }, [
    watchProductor,
    dataState,
    setValue,
    getProductorObj,
    showNewValueInputs.productor,
    clearErrors,
  ]);

  // useEffect para agroBusiness -> productor + grupo (sincronización bidireccional)
  useEffect(() => {
    if (isUpdatingProductorAgroBusiness) return;

    if (
      watchAgroBusiness &&
      watchAgroBusiness.value &&
      dataState?.agroBusiness &&
      !showNewValueInputs.productor
    ) {
      setIsUpdatingProductorAgroBusiness(true);

      clearErrors(["productor", "agroBusiness", "group"]);

      const agroBusinessObj = getAgroBusinessObj();
      if (
        agroBusinessObj?.productorID &&
        agroBusinessObj.productorID.length > 0
      ) {
        const firstProductor = agroBusinessObj.productorID[0];
        setValue("productor", firstProductor);

        if (firstProductor.group && firstProductor.group.length > 0) {
          const firstGroup = firstProductor.group[0];
          setValue("group", firstGroup);
          setValue("group_custom", firstGroup.label);
        } else {
          clearSelects(setValue, ["group"]);
          setValue("group_custom", "");
        }
      } else {
        clearSelects(setValue, ["productor", "group"]);
        setValue("group_custom", "");
      }

      setTimeout(() => setIsUpdatingProductorAgroBusiness(false), 0);
    }
  }, [
    watchAgroBusiness,
    dataState,
    setValue,
    clearErrors,
    getAgroBusinessObj,
    showNewValueInputs.productor,
  ]);

  useEffect(() => {
    if (
      watchOrchard &&
      watchOrchard.value &&
      dataState?.orchard &&
      !showNewValueInputs.csg &&
      !showNewValueInputs.orchard
    ) {
      // Limpiar errores de react-hook-form cuando se selecciona un huerto válido
      clearErrors(["orchard", "csg"]);

      const orchardObj = getOrchardObj();
      if (orchardObj?.csg && orchardObj.csg.length > 0) {
        if (orchardObj.csg.length === 1) {
          const firstCsg = orchardObj.csg[0];
          setValue("csg", firstCsg.value || "");
        } else {
          setValue("csg", null);
        }
      } else {
        setValue("csg", "");
      }
    } else if (!showNewValueInputs.orchard) {
      setValue("csg", "");
    }
  }, [
    watchOrchard,
    dataState,
    getOrchardObj,
    showNewValueInputs.csg,
    showNewValueInputs.orchard,
    setValue,
    clearErrors,
  ]);

  // Función para validar CSG en la lista de prestados
  const validateCsgInPrestados = useCallback(
    (csgValue) => {
      if (!csgValue || !allCsgOptions.length) {
        setCsgValidationState({
          isValidating: false,
          foundMatch: false,
          matchedCsg: null,
          isNewCsg: false,
        });
        return;
      }

      setCsgValidationState((prev) => ({ ...prev, isValidating: true }));

      const foundCsg = allCsgOptions.find(
        (option) =>
          option.csgOriginal.value.toLowerCase() === csgValue.toLowerCase()
      );

      setTimeout(() => {
        if (foundCsg) {
          setCsgValidationState({
            isValidating: false,
            foundMatch: true,
            matchedCsg: foundCsg,
            isNewCsg: false,
          });
        } else {
          setCsgValidationState({
            isValidating: false,
            foundMatch: false,
            matchedCsg: null,
            isNewCsg: true,
          });
        }
      }, 100);
    },
    [allCsgOptions]
  );

  // Función para manejar el blur del input CSG
  const handleCsgBlur = useCallback(() => {
    if (showNewValueInputs.orchard && !showNewValueInputs.csg && watchCsg) {
      if (csgDebounceTimeout) {
        clearTimeout(csgDebounceTimeout);
        setCsgDebounceTimeout(null);
      }
      validateCsgInPrestados(watchCsg);
    }
  }, [
    showNewValueInputs.orchard,
    showNewValueInputs.csg,
    watchCsg,
    validateCsgInPrestados,
    csgDebounceTimeout,
  ]);

  useEffect(() => {
    if (
      showNewValueInputs.orchard &&
      !showNewValueInputs.csg &&
      watchCsg &&
      watchCsg.length > 0
    ) {
      if (csgDebounceTimeout) {
        clearTimeout(csgDebounceTimeout);
      }

      const timeoutId = setTimeout(() => {
        validateCsgInPrestados(watchCsg);
      }, 1000);

      setCsgDebounceTimeout(timeoutId);

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    } else if (!watchCsg || watchCsg.length === 0) {
      setCsgValidationState({
        isValidating: false,
        foundMatch: false,
        matchedCsg: null,
        isNewCsg: false,
      });
    }
  }, [
    watchCsg,
    showNewValueInputs.orchard,
    showNewValueInputs.csg,
    validateCsgInPrestados,
  ]);

  // useEffect para generar todas las opciones de CSG Asociado
  useEffect(() => {
    if (dataState?.orchard && Array.isArray(dataState.orchard)) {
      const allCsgOptionsArray = [];

      dataState.orchard.forEach((orchard) => {
        if (orchard.csg && Array.isArray(orchard.csg)) {
          orchard.csg
            .filter((item) => item.value !== "" && item.label !== "")
            .forEach((csg) => {
              allCsgOptionsArray.push({
                label: `${csg.value}, ${orchard.label}`,
                value: csg.value,
                csgOriginal: csg,
                orchardOriginal: orchard,
              });
            });
        }
      });

      setAllCsgOptions(alphabeticalSort(allCsgOptionsArray, "label"));
    } else {
      setAllCsgOptions([]);
    }
  }, [dataState]);

  // useEffect para filtrar CSG Asociado (excluir CSGs del huerto seleccionado) y CSGs del huerto seleccionado
  useEffect(() => {
    if (watchOrchard && watchOrchard.value && allCsgOptions.length > 0) {
      const filteredPrestado = allCsgOptions.filter(
        (csgOption) => csgOption.orchardOriginal.value !== watchOrchard.value
      );
      setFilteredCsgPrestadoOptions(
        alphabeticalSort(filteredPrestado, "label")
      );

      const orchardCsgs = allCsgOptions
        .filter(
          (csgOption) => csgOption.orchardOriginal.value === watchOrchard.value
        )
        .map((item) => item.csgOriginal);
      setFilteredOrchardCsgOptions(alphabeticalSort(orchardCsgs, "label"));
    } else {
      setFilteredCsgPrestadoOptions(allCsgOptions);
      setFilteredOrchardCsgOptions([]);
    }
  }, [watchOrchard, allCsgOptions]);

  // Función para validar ProductorID en la lista existente
  const validateProductorIdExists = useCallback(
    (productorValue) => {
      if (!productorValue || !productorIdOptions.length) {
        setProductorValidationState({
          isValidating: false,
          foundMatch: false,
          matchedProductor: null,
          isNewProductor: false,
        });
        return;
      }

      setProductorValidationState((prev) => ({ ...prev, isValidating: true }));

      const cleanedInputRut = cleanRutForComparison(productorValue);

      const foundProductor = productorIdOptions.find((option) => {
        const cleanedOptionValue = cleanRutForComparison(option.value);
        const cleanedOptionLabel = cleanRutForComparison(option.label);
        return (
          cleanedOptionValue === cleanedInputRut ||
          cleanedOptionLabel === cleanedInputRut
        );
      });

      setTimeout(() => {
        if (foundProductor) {
          setProductorValidationState({
            isValidating: false,
            foundMatch: true,
            matchedProductor: foundProductor,
            isNewProductor: false,
          });
        } else {
          setProductorValidationState({
            isValidating: false,
            foundMatch: false,
            matchedProductor: null,
            isNewProductor: true,
          });
        }
      }, 100);
    },
    [productorIdOptions]
  );

  // Función para manejar el blur del input ProductorID
  const handleProductorBlur = useCallback(() => {
    if (showNewValueInputs.productor && watchProductor_custom) {
      if (productorDebounceTimeout) {
        clearTimeout(productorDebounceTimeout);
        setProductorDebounceTimeout(null);
      }
      validateProductorIdExists(watchProductor_custom);
    }
  }, [
    showNewValueInputs.productor,
    watchProductor_custom,
    validateProductorIdExists,
    productorDebounceTimeout,
  ]);

  // Función para formatear RUT mientras se escribe
  const handleProductorChange = useCallback(
    (e) => {
      const rawValue = e.target.value;
      const formattedRut = formatRutWithoutDots(rawValue);
      setValue("productor_custom", formattedRut);
    },
    [setValue]
  );

  // useEffect para validación de ProductorID con debounce
  useEffect(() => {
    if (
      showNewValueInputs.productor &&
      watchProductor_custom &&
      watchProductor_custom.length > 0
    ) {
      if (productorDebounceTimeout) {
        clearTimeout(productorDebounceTimeout);
      }

      const timeoutId = setTimeout(() => {
        validateProductorIdExists(watchProductor_custom);
      }, 1000);

      setProductorDebounceTimeout(timeoutId);

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    } else if (!watchProductor_custom || watchProductor_custom.length === 0) {
      setProductorValidationState({
        isValidating: false,
        foundMatch: false,
        matchedProductor: null,
        isNewProductor: false,
      });
    }
  }, [
    watchProductor_custom,
    showNewValueInputs.productor,
    validateProductorIdExists,
  ]);

  // useEffect para specie -> variety
  useEffect(() => {
    if (watchSpecie && watchSpecie.value && dataState?.specie) {
      clearErrors(["specie", "variety", "product"]);

      clearSelects(setValue, ["variety", "product"]);
      const specieObj = getSpecieObj();
      if (specieObj?.variety) {
        setFilteredVarietyOptions(alphabeticalSort(specieObj.variety, "label"));
      } else {
        setFilteredVarietyOptions([]);
      }
    } else {
      clearSelects(setValue, ["variety", "product"]);
      setFilteredVarietyOptions([]);
      setFilteredProductLineOptions([]);
    }
  }, [watchSpecie, dataState, setValue, clearErrors, getSpecieObj]);

  // useEffect para variety -> productLine (con auto-selección)
  useEffect(() => {
    if (watchVariety && watchVariety.value) {
      clearErrors(["variety", "product"]);

      clearSelects(setValue, ["product"]);
      const varietyObj = getVarietyObj();
      if (varietyObj?.productLine && varietyObj.productLine.length > 0) {
        const sortedProductLines = alphabeticalSort(varietyObj.productLine, "label");
        setFilteredProductLineOptions(sortedProductLines);
        
        // Auto-seleccionar la primera línea de producto cuando se selecciona una variedad
        if (sortedProductLines.length === 1) {
          // Si solo hay una opción, seleccionarla automáticamente
          setValue("product", sortedProductLines[0]);
        } else if (sortedProductLines.length > 1) {
          // Si hay múltiples opciones, seleccionar la primera por defecto
          setValue("product", sortedProductLines[0]);
        }
      } else {
        setFilteredProductLineOptions([]);
      }
    } else {
      clearSelects(setValue, ["product"]);
      setFilteredProductLineOptions([]);
    }
  }, [watchVariety, setValue, clearErrors, getVarietyObj]);

  // useEffect para group -> orchard (filtrar huertos por grupo con auto-selección)
  useEffect(() => {
    if (watchGroup && watchGroup.value && !showNewValueInputs.productor) {
      clearErrors(["group", "orchard"]);

      // Limpiar selección de huerto cuando cambie el grupo
      clearSelects(setValue, ["orchard"]);

      const groupObj = getGroupObj();
      
      if (groupObj?.orchard && groupObj.orchard.length > 0) {
        const sortedOrchards = alphabeticalSort(groupObj.orchard, "label");
        setFilteredOrchardOptions(sortedOrchards);
        
        // Auto-seleccionar el huerto cuando hay solo una opción
        if (sortedOrchards.length === 1) {
          // Si solo hay una opción, seleccionarla automáticamente
          setValue("orchard", sortedOrchards[0]);
        }
      } else {
        setFilteredOrchardOptions([]);
      }
    } else {
      // Si no hay grupo seleccionado o se está usando nuevo productor, mostrar todos los huertos
      setFilteredOrchardOptions(orchardOptions);
    }
  }, [watchGroup, dataState, setValue, clearErrors, getGroupObj, orchardOptions, showNewValueInputs.productor]);

  // Función para validar campos antes del submit
  const validateBeforeSubmit = (values) => {
    if (showNewValueInputs.productor) {
      if (!values?.productor_custom || values.productor_custom.trim() === "") {
        setErrorState({
          error: true,
          message: "El Productor ID no puede estar vacío",
        });
        return false;
      }

      const productorValue = values.productor_custom.trim();
      const cleanedInputRut = cleanRutForComparison(productorValue);

      const foundProductor = productorIdOptions.find((option) => {
        const cleanedOptionValue = cleanRutForComparison(option.value);
        const cleanedOptionLabel = cleanRutForComparison(option.label);
        return (
          cleanedOptionValue === cleanedInputRut ||
          cleanedOptionLabel === cleanedInputRut
        );
      });

      if (foundProductor) {
        setErrorState({
          error: true,
          message: `El Productor ID "${productorValue}" ya existe en el sistema. No puede crear un productor duplicado.`,
        });
        return false;
      }
    }

    if (showNewValueInputs.orchard) {
      if (!values?.orchard_custom || values.orchard_custom.trim() === "") {
        setErrorState({
          error: true,
          message: "El nombre del huerto no puede estar vacío",
        });
        return false;
      }
    }

    if (showNewValueInputs.csg) {
      if (!values?.csg_prestado?.value) {
        setErrorState({
          error: true,
          message: "Debe seleccionar un CSG Asociado",
        });
        return false;
      }
    } else {
      const csgValue =
        typeof values?.csg === "object" && values?.csg?.value
          ? values.csg.value
          : values?.csg;

      if (
        !csgValue ||
        (typeof csgValue === "string" && csgValue.trim() === "")
      ) {
        setErrorState({
          error: true,
          message: "El campo CSG no puede estar vacío",
        });
        return false;
      }

      if (showNewValueInputs.orchard) {
        const csgValueToCheck =
          typeof csgValue === "string" ? csgValue.trim() : csgValue;
        const foundInPrestados = allCsgOptions.find(
          (option) =>
            option.csgOriginal.value.toLowerCase() ===
            csgValueToCheck.toLowerCase()
        );

        if (foundInPrestados) {
          setErrorState({
            error: true,
            message: `El CSG "${csgValueToCheck}" ya existe en el huerto "${foundInPrestados.orchardOriginal.label}". Debe usar la opción "CSG Asociado" para seleccionarlo.`,
          });
          return false;
        }
      }
    }
    // Pactado y fechas solo son obligatorios si la línea se ve en planificación
    if (!showPlanningHarvestState) {
      return true;
    }

    // Validación para el rango de fechas de cosecha
    if (
      !values?.harvestDateRange ||
      !values.harvestDateRange.begin ||
      !values.harvestDateRange.end
    ) {
      setErrorState({
        error: true,
        message: "Debe seleccionar un rango de fechas",
      });
      return false;
    }

    if (values.harvestDateRange.begin > values.harvestDateRange.end) {
      setErrorState({
        error: true,
        message:
          "La fecha de inicio de cosecha no puede ser posterior a la fecha de fin",
      });
      return false;
    }

    // Validación para el campo pactado (debe ser un número válido)
    if (!values?.pactado || values.pactado.trim() === "") {
      setErrorState({
        error: true,
        message: "El campo Pactado es obligatorio",
      });
      return false;
    }

    const pactadoNumber = Number(values.pactado);
    if (isNaN(pactadoNumber) || pactadoNumber < 0) {
      setErrorState({
        error: true,
        message: "El campo Pactado debe ser un número válido mayor o igual a 0",
      });
      return false;
    }

    return true;
  };

  const handleShowPlanningHarvestChange = (event) => {
    const checked = event.target.checked;
    setShowPlanningHarvestState(checked);
    if (!checked) {
      // Dejan de ser obligatorios: limpiar los errores que ya se mostraron
      clearErrors(["pactado", "harvestDateRange"]);
    }
  };

  const onSubmit = async (values) => {
    if (!validateBeforeSubmit(values)) {
      return;
    }

    setLoadingQueryState(true);
    const body = {
      userId,
      client: values?.client.labelOriginal || null,
      clientValue: values?.client ? values?.client?.value : null,
      productorID: showNewValueInputs.productor
        ? values?.productor_custom || null
        : values?.productor?.value || null,
      agroBusiness: showNewValueInputs.productor
        ? values?.agroBusiness_custom || null
        : values?.agroBusiness?.label || null,
      agroBusinessValue: showNewValueInputs.productor
        ? null
        : values?.agroBusiness?.value || null,
      group: showNewValueInputs.productor
        ? values?.group_custom || null
        : values?.group?.label || null,
      groupValue: showNewValueInputs.productor
        ? null
        : values?.group?.value || null,
      csg: showNewValueInputs.csg
        ? values?.csg_prestado?.value || null
        : typeof values?.csg === "object" && values?.csg?.value
        ? values.csg.value
        : values?.csg || null,
      orchard: showNewValueInputs.orchard
        ? values?.orchard_custom || null
        : values?.orchard?.label || null,
      orchardValue: showNewValueInputs.orchard
        ? null
        : values?.orchard?.value || null,
      specie: values?.specie ? values?.specie?.label : null,
      specieValue: values?.specie ? values?.specie?.value : null,
      variety: values?.variety ? values?.variety?.label : null,
      varietyValue: values?.variety ? values?.variety?.value : null,
      productLine: values?.product?.label || null,
      productLineValue: values?.product?.value || null,
      technicBoss: values?.boss?.label || null,
      technicBossValue: values?.boss?.value || null,
      dateBegin: formatDateToMMDDYYYY(values?.harvestDateRange?.begin),
      dateEnd: formatDateToMMDDYYYY(values?.harvestDateRange?.end),
      kilos:
        values?.pactado && !isNaN(Number(values.pactado))
          ? Number(values.pactado)
          : null,
      showPlanningHarvest: showPlanningHarvestState,
      showTechnicalReport: showTechnicalReportState,
    };
    try {
      // const response = {
      //   status: 200,
      //   data: {
      //     success: true,
      //     message: "Datos guardados correctamente",
      //   },
      // };
      const response = await axiosContext.authAxios.post(
        endpointNamePost,
        body
      );
      if (response.status === 200) {
        if (response.data.success) {
          setSuccessQueryState(true);
          clearForm();
        } else {
          setErrorState({
            error: true,
            message: response.data.message || "Error al guardar los datos",
          });
        }
      }

      setLoadingQueryState(false);
    } catch (err) {
      console.log("Error", err.request.status);
      if (err.request.status === 500) {
        setErrorState({
          error: true,
          message: "Error del servidor, intente nuevamente más tarde.",
        });
      }

      setLoadingQueryState(false);
    }

    setLoadingQueryState(false);
  };

  const getDataAsync = useCallback(async () => {
    setLoadingDataState(true);
    try {
      let response = await axiosContext.authAxios.get(endpointName);

      const { data, status } = response;

      if (status === 200) {
        setDataState(data);
        setupOptionsFromData(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingDataState(false);
    }
  }, [axiosContext, setLoadingDataState, setDataState]);
  useEffect(() => {
    getDataAsync();
  }, [getDataAsync]);

  useEffect(() => {
    return () => {
      if (csgDebounceTimeout) {
        clearTimeout(csgDebounceTimeout);
      }
      if (productorDebounceTimeout) {
        clearTimeout(productorDebounceTimeout);
      }
    };
  }, [csgDebounceTimeout, productorDebounceTimeout]);

  const setupOptionsFromData = useCallback((data) => {
    if (!data) return;

    const clients = data.client || [];
    const clientsFormatted = clients.map((client) => {
      if (client.value === "Garces_Fruit") {
        return { ...client, label: "Propios", labelOriginal: client.label };
      } else if (client.value === "Garces_Fruit_2") {
        return { ...client, label: "Externos", labelOriginal: client.label };
      }
      return client;
    });
    setClientOptions(alphabeticalSort(clientsFormatted, "label"));

    const productorIds = data.productorID || [];
    setProductorIdOptions(alphabeticalSort(productorIds, "label"));

    const agroBusinesses = data.agroBusiness || [];
    setAgroBusinessOptions(alphabeticalSort(agroBusinesses, "label"));

    const orchards = data.orchard || [];
    setOrchardOptions(alphabeticalSort(orchards, "label"));
    setFilteredOrchardOptions(alphabeticalSort(orchards, "label")); // Inicializar con todas las opciones

    const species = data.specie || [];
    setSpeciesOptions(alphabeticalSort(species, "label"));

    const technicBosses = data.technicBoss || [];
    setTechnicBossOptions(alphabeticalSort(technicBosses, "label"));

    setFilteredVarietyOptions([]);
    setFilteredProductLineOptions([]);
  }, []);

  const handleCloseSnackBarsComponent = () => {
    setErrorState(false);
  };

  const onCloseSnack = () => {
    setSuccessQueryState(false);
  };

  const clearForm = () => {
    reset({
      client: null,
      productor: null,
      agroBusiness: null,
      csg: "",
      orchard: null,
      specie: null,
      variety: null,
      product: null,
      boss: null,
      group: null,
      client_custom: "",
      productor_custom: "",
      agroBusiness_custom: "",
      csg_prestado: null,
      orchard_custom: "",
      specie_custom: "",
      variety_custom: "",
      boss_custom: "",
      group_custom: "",
      harvestDateRange: { begin: null, end: null },
      pactado: "",
    });
    setShowNewValueInputs({
      productor: false,
      csg: false,
      orchard: false,
    });
    setShowPlanningHarvestState(true);
    setShowTechnicalReportState(true);
    // Resetear opciones filtradas
    setFilteredOrchardOptions(orchardOptions);
  };
  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            <Card elevation={0}>
              <CardHeaderWidget
                title={viewsTexts.titles.planningHarvest.title}
                subheader={<BreadCrumbs subTitle="" />}
              />
              {/* <Divider /> */}
              <CardContent>
                <BsRow>
                  <BsCol class="col-md-12">
                    <form autoComplete="on" onSubmit={handleSubmit(onSubmit)}>
                      <BsRow>
                        <BsCol class="col-md-12">
                          <SelectMultipleWidgetFilter
                            disabled={loadingDataState}
                            options={clientOptions}
                            titleOption="label"
                            control={control}
                            name="client"
                            labeltext="Seleccione Cliente"
                            errors={errors.client}
                            defaultValue={null}
                            loading={loadingDataState}
                            req
                            withDefault
                            variant="outlined"
                            placeholder="Cliente"
                            display="vertical"
                            minheight={false}
                            isClearable={true}
                            isSearchable
                            sizeSelect={"large"}
                            isMulti={false}
                          />
                        </BsCol>
                      </BsRow>
                      {/* Row: Productor ID y Agrícola */}
                      <BsRow class="mt-4">
                        <BsCol class="col-md-6">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showNewValueInputs.productor}
                                onChange={handleNewValueCheckboxChange(
                                  "productor"
                                )}
                                color="primary"
                              />
                            }
                            label="Nuevo Rut"
                            sx={{ marginBottom: 0 }}
                          />
                          {showNewValueInputs.productor ? (
                            <div className="mt-[1.3rem]">
                              <TextFieldWidgetText2
                                name="productor_custom"
                                labeltext="Rut"
                                placeholder="ej: 12345678-9"
                                req
                                defaultValue=""
                                control={control}
                                errors={errors.productor_custom}
                                display="vertical"
                                sizeInput="large"
                                onBlur={handleProductorBlur}
                                onChange={handleProductorChange}
                              />
                            </div>
                          ) : (
                            <SelectMultipleWidgetFilter
                              disabled={loadingDataState}
                              options={productorIdOptions}
                              titleOption="label"
                              control={control}
                              name="productor"
                              labeltext="Seleccione Rut"
                              errors={errors.productor}
                              defaultValue={null}
                              loading={loadingDataState}
                              req
                              withDefault
                              variant="outlined"
                              placeholder="Rut"
                              display="vertical"
                              minheight={false}
                              isClearable={true}
                              isSearchable
                              sizeSelect={"large"}
                              isMulti={false}
                            />
                          )}
                        </BsCol>
                        <BsCol class="col-md-6">
                          {showNewValueInputs.productor ? (
                            <div className="mt-[4rem]">
                              <TextFieldWidgetText2
                                name="agroBusiness_custom"
                                labeltext="Razón Social"
                                placeholder="ej: Agrícola Los Paltos"
                                req={false}
                                defaultValue=""
                                control={control}
                                errors={errors.agroBusiness_custom}
                                display="vertical"
                                sizeInput="large"
                              />
                            </div>
                          ) : (
                            <SelectMultipleWidgetFilter
                              disabled={loadingDataState}
                              options={agroBusinessOptions}
                              titleOption="label"
                              control={control}
                              name="agroBusiness"
                              labeltext="Seleccione Razón Social"
                              errors={errors.agroBusiness}
                              defaultValue={null}
                              loading={loadingDataState}
                              req
                              withDefault
                              variant="outlined"
                              placeholder="Razón Social"
                              display="vertical"
                              minheight={false}
                              isClearable={true}
                              isSearchable
                              sizeSelect={"large"}
                              isMulti={false}
                              containerStyle={{
                                marginTop: "42px",
                              }}
                            />
                          )}
                        </BsCol>
                      </BsRow>
                      {/* Alert de validación ProductorID */}
                      {showNewValueInputs.productor &&
                        watchProductor_custom && (
                          <BsRow class="mt-2">
                            <BsCol class="col-md-12">
                              {productorValidationState.isValidating ? (
                                <Alert
                                  severity="warning"
                                  variant="filled"
                                  sx={{
                                    backgroundColor: "#ffe294",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  Validando rut...
                                </Alert>
                              ) : productorValidationState.foundMatch ? (
                                <Alert
                                  severity="error"
                                  variant="filled"
                                  sx={{
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <strong>Rut Existente:</strong> El Rut "
                                  {watchProductor_custom}" ya existe en el
                                  sistema. Selecciónelo en la lista desmarcando
                                  nuevo rut.
                                </Alert>
                              ) : productorValidationState.isNewProductor ? (
                                <Alert
                                  severity="success"
                                  variant="filled"
                                  sx={{
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <strong>Rut Nuevo:</strong> Se creará un nuevo
                                  Rut "{watchProductor_custom}
                                  ".
                                </Alert>
                              ) : null}
                            </BsCol>
                          </BsRow>
                        )}
                      <BsRow class="mt-4">
                        <BsCol class="col-md-12">
                          <div className="mt-[1.2rem]">
                            <TextFieldWidgetText2
                              name="group_custom"
                              labeltext="Grupo"
                              placeholder={
                                showNewValueInputs.productor
                                  ? "ej: Viña Siegel Sa"
                                  : "Se autocompletará según Productor ID o Agrícola"
                              }
                              req={false}
                              defaultValue=""
                              control={control}
                              errors={errors.group_custom}
                              display="vertical"
                              sizeInput="large"
                              disabled={!showNewValueInputs.productor}
                            />
                          </div>
                        </BsCol>
                      </BsRow>
                      {/* Row: Huerto y CSG */}
                      <BsRow class="mt-4">
                        <BsCol class="col-md-6">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showNewValueInputs.orchard}
                                onChange={handleNewValueCheckboxChange(
                                  "orchard"
                                )}
                                color="primary"
                              />
                            }
                            label="Nuevo Huerto"
                            sx={{ marginBottom: 0 }}
                          />
                          {showNewValueInputs.orchard ? (
                            <div className="mt-[1.3rem]">
                              <TextFieldWidgetText2
                                name="orchard_custom"
                                labeltext="Nombre del Huerto"
                                placeholder="ej: Huerto Los Aromos"
                                req
                                defaultValue=""
                                control={control}
                                errors={errors.orchard_custom}
                                display="vertical"
                                sizeInput="large"
                              />
                            </div>
                          ) : (
                            <SelectMultipleWidgetFilter
                              disabled={loadingDataState || (!watchGroup?.value && !showNewValueInputs.productor)}
                              options={filteredOrchardOptions}
                              titleOption="label"
                              control={control}
                              name="orchard"
                              labeltext="Seleccione Huerto"
                              errors={errors.orchard}
                              defaultValue={null}
                              loading={loadingDataState}
                              req
                              withDefault
                              variant="outlined"
                              placeholder="Huerto"
                              display="vertical"
                              minheight={false}
                              isClearable={true}
                              isSearchable
                              sizeSelect={"large"}
                              isMulti={false}
                            />
                          )}
                        </BsCol>
                        <BsCol class="col-md-6">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showNewValueInputs.csg}
                                onChange={handleNewValueCheckboxChange("csg")}
                                color="primary"
                              />
                            }
                            label="CSG Asociado"
                            sx={{ marginBottom: 0 }}
                          />

                          {showNewValueInputs.orchard ? (
                            showNewValueInputs.csg ? (
                              <SelectMultipleWidgetFilter
                                disabled={loadingDataState}
                                options={filteredCsgPrestadoOptions}
                                titleOption="label"
                                control={control}
                                name="csg_prestado"
                                labeltext="Seleccione CSG Asociado"
                                errors={errors.csg_prestado}
                                defaultValue={null}
                                loading={loadingDataState}
                                req
                                withDefault
                                variant="outlined"
                                placeholder="CSG, Huerto"
                                display="vertical"
                                minheight={false}
                                isClearable={true}
                                isSearchable
                                sizeSelect={"large"}
                                isMulti={false}
                              />
                            ) : (
                              // Nuevo Huerto y el CSG Nuevo (con validación)
                              <div
                                className={
                                  showNewValueInputs.orchard
                                    ? "mt-[1.3rem]"
                                    : ""
                                }
                              >
                                <TextFieldWidgetText2
                                  name="csg"
                                  labeltext="CSG"
                                  placeholder="ej: 1001"
                                  req
                                  defaultValue=""
                                  control={control}
                                  errors={errors.csg}
                                  display="vertical"
                                  sizeInput="large"
                                  onBlur={handleCsgBlur}
                                />
                              </div>
                            )
                          ) : showNewValueInputs.csg ? (
                            // Huerto Existente y el CSG Asociado
                            <SelectMultipleWidgetFilter
                              disabled={loadingDataState}
                              options={filteredCsgPrestadoOptions}
                              titleOption="label"
                              control={control}
                              name="csg_prestado"
                              labeltext="Seleccione CSG Asociado"
                              errors={errors.csg_prestado}
                              defaultValue={null}
                              loading={loadingDataState}
                              req
                              withDefault
                              variant="outlined"
                              placeholder="CSG, Huerto"
                              display="vertical"
                              minheight={false}
                              isClearable={true}
                              isSearchable
                              sizeSelect={"large"}
                              isMulti={false}
                            />
                          ) : (
                            // Huerto Existente y el CSG del huerto
                            <>
                              {filteredOrchardCsgOptions.length > 1 ? (
                                <SelectMultipleWidgetFilter
                                  disabled={
                                    loadingDataState || !watchOrchard?.value
                                  }
                                  options={filteredOrchardCsgOptions}
                                  titleOption="label"
                                  control={control}
                                  name="csg"
                                  labeltext="Seleccione CSG del Huerto"
                                  errors={errors.csg}
                                  defaultValue={null}
                                  loading={loadingDataState}
                                  req
                                  withDefault
                                  variant="outlined"
                                  placeholder={
                                    watchOrchard?.value
                                      ? "Seleccione un CSG"
                                      : "Primero seleccione un huerto"
                                  }
                                  display="vertical"
                                  minheight={false}
                                  isClearable={true}
                                  isSearchable
                                  sizeSelect={"large"}
                                  isMulti={false}
                                  getOptionValue={(option) => option.value}
                                />
                              ) : (
                                // Si el huerto tiene un solo CSG, mostrar input deshabilitado
                                <div className="mt-[1.3rem]">
                                  <TextFieldWidgetText2
                                    name="csg"
                                    labeltext="CSG"
                                    placeholder={
                                      watchOrchard?.value
                                        ? "CSG"
                                        : "Primero seleccione un huerto"
                                    }
                                    req={false}
                                    defaultValue=""
                                    control={control}
                                    errors={errors.csg}
                                    display="vertical"
                                    sizeInput="large"
                                    disabled={true}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </BsCol>
                      </BsRow>
                      {/* Alert de validación CSG */}
                      {showNewValueInputs.orchard &&
                        !showNewValueInputs.csg &&
                        watchCsg && (
                          <BsRow class="mt-2">
                            <BsCol class="col-md-12">
                              {csgValidationState.isValidating ? (
                                <Alert
                                  severity="warning"
                                  variant="filled"
                                  sx={{
                                    backgroundColor: "#ffe294",
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  Validando CSG...
                                </Alert>
                              ) : csgValidationState.foundMatch ? (
                                <Alert
                                  severity="error"
                                  variant="filled"
                                  sx={{
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <strong>CSG Existente:</strong> El CSG "
                                  {watchCsg}" ya existe en el huerto "
                                  {
                                    csgValidationState.matchedCsg
                                      ?.orchardOriginal.label
                                  }
                                  ". Debe activar la opción "CSG Asociado" para
                                  seleccionarlo.
                                </Alert>
                              ) : csgValidationState.isNewCsg ? (
                                <Alert
                                  severity="success"
                                  variant="filled"
                                  sx={{
                                    justifyContent: "center",
                                    width: "100%",
                                  }}
                                >
                                  <strong>CSG Nuevo:</strong> Se creará un nuevo
                                  CSG "{watchCsg}" para el huerto "
                                  {watchOrchardCustom}".
                                </Alert>
                              ) : null}
                            </BsCol>
                          </BsRow>
                        )}

                      {showNewValueInputs.csg && (
                        <BsRow class="mt-4">
                          <BsCol class="col-md-12">
                            <Alert
                              severity="info"
                              variant="filled"
                              sx={{
                                justifyContent: "center",
                                width: "100%",
                              }}
                              icon={false}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "20px",
                                }}
                              >
                                <div>
                                  <strong>CSG:</strong>{" "}
                                  {watchCsg_prestado
                                    ? watchCsg_prestado.csgOriginal.value
                                    : "-"}
                                </div>
                                <div>
                                  <strong>Huerto:</strong>{" "}
                                  {showNewValueInputs.orchard
                                    ? watchOrchardCustom || "-"
                                    : watchOrchard?.label || "-"}
                                </div>
                                <div>
                                  <strong>Grupo:</strong>{" "}
                                  {watchGroup?.label
                                    ? watchGroup?.label
                                    : watchGroup_custom
                                    ? watchGroup_custom
                                    : "-"}
                                </div>
                                <div>
                                  <strong>Agrícola:</strong>{" "}
                                  {watchAgroBusiness?.label
                                    ? watchAgroBusiness?.label
                                    : watchAgroBusiness_custom
                                    ? watchAgroBusiness_custom
                                    : "-"}
                                </div>
                                <div>
                                  <strong>Productor ID:</strong>{" "}
                                  {!showNewValueInputs.productor
                                    ? watchProductor?.label
                                    : watchProductor_custom
                                    ? watchProductor_custom
                                    : "-"}
                                </div>
                              </div>
                            </Alert>
                          </BsCol>
                        </BsRow>
                      )}

                      {/* Row: Especie y Variedad */}
                      <BsRow>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            disabled={loadingDataState}
                            options={speciesOptions}
                            titleOption="label"
                            control={control}
                            name="specie"
                            labeltext="Seleccione Especie"
                            errors={errors.specie}
                            defaultValue={null}
                            loading={loadingDataState}
                            req
                            withDefault
                            variant="outlined"
                            placeholder="Especie"
                            display="vertical"
                            minheight={false}
                            isClearable={true}
                            isSearchable
                            sizeSelect={"large"}
                            isMulti={false}
                            containerStyle={{
                              marginTop: "42px",
                            }}
                          />
                        </BsCol>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            disabled={loadingDataState || !watchSpecie?.value}
                            options={filteredVarietyOptions}
                            titleOption="label"
                            control={control}
                            name="variety"
                            labeltext="Seleccione Variedad"
                            errors={errors.variety}
                            defaultValue={null}
                            loading={loadingDataState}
                            req
                            withDefault
                            variant="outlined"
                            placeholder={
                              watchSpecie?.value
                                ? "Variedad"
                                : "Primero seleccione una especie"
                            }
                            display="vertical"
                            minheight={false}
                            isClearable={true}
                            isSearchable
                            sizeSelect={"large"}
                            isMulti={false}
                            containerStyle={{
                              marginTop: "42px",
                            }}
                          />
                        </BsCol>
                      </BsRow>
                      <BsRow>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            disabled={loadingDataState}
                            options={filteredProductLineOptions}
                            titleOption="label"
                            control={control}
                            name="product"
                            labeltext="Seleccione Línea de Producto"
                            errors={errors.product}
                            defaultValue={null}
                            loading={loadingDataState}
                            req
                            withDefault
                            variant="outlined"
                            placeholder="Línea de Producto"
                            display="vertical"
                            minheight={false}
                            isClearable={true}
                            isSearchable
                            sizeSelect={"large"}
                            isMulti={false}
                            containerStyle={{
                              marginTop: "42px",
                            }}
                          />
                        </BsCol>
                        <BsCol class="col-md-6">
                          <SelectMultipleWidgetFilter
                            disabled={loadingDataState}
                            options={technicBossOptions}
                            titleOption="label"
                            control={control}
                            name="boss"
                            labeltext="Seleccione Jefe Técnico"
                            errors={errors.boss}
                            defaultValue={[]}
                            loading={loadingDataState}
                            req
                            withDefault
                            variant="outlined"
                            placeholder="Jefe Técnico"
                            display="vertical"
                            minheight={false}
                            isClearable={true}
                            isSearchable
                            sizeSelect={"large"}
                            isMulti={false}
                            containerStyle={{
                              marginTop: "42px",
                            }}
                          />
                        </BsCol>
                      </BsRow>
                      {/* Row: visibilidad de la línea */}
                      <BsRow class="mt-4">
                        <BsCol class="col-md-6">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showPlanningHarvestState}
                                onChange={handleShowPlanningHarvestChange}
                                color="primary"
                              />
                            }
                            label="Ver en Planificación de cosecha"
                            sx={{ marginBottom: 0 }}
                          />
                          {!showPlanningHarvestState && (
                            <div className="text-sm text-gray-500">
                              Pactado y fechas opcionales: no se crea
                              planificación para esta línea.
                            </div>
                          )}
                        </BsCol>
                        <BsCol class="col-md-6">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showTechnicalReportState}
                                onChange={(e) =>
                                  setShowTechnicalReportState(e.target.checked)
                                }
                                color="primary"
                              />
                            }
                            label="Ver en Informe técnico"
                            sx={{ marginBottom: 0 }}
                          />
                        </BsCol>
                      </BsRow>
                      <BsRow>
                        <BsCol class="col-md-6">
                          <div className="mt-[4.3rem]">
                            <TextFieldWidgetText2
                              name="pactado"
                              labeltext="Pactado"
                              placeholder="ej: 4000"
                              req={showPlanningHarvestState}
                              defaultValue=""
                              control={control}
                              errors={errors.pactado}
                              display="vertical"
                              sizeInput="large"
                              // disabled={true}
                            />
                          </div>
                        </BsCol>
                        <BsCol class="col-md-6">
                          <TextFieldWidgetDateRange
                            name="harvestDateRange"
                            labeltext="Fecha de Cosecha"
                            req={showPlanningHarvestState}
                            defaultValue={{ begin: null, end: null }}
                            control={control}
                            errors={errors.harvestDateRange}
                            display="horizontal"
                            sizeSelect={"large"}
                            beginSinceDate={(() => {
                              const yesterday = new Date();
                              yesterday.setDate(yesterday.getDate() - 1);
                              // inicio del día para evitar problemas de zona horaria
                              yesterday.setHours(0, 0, 0, 0);
                              return yesterday;
                            })()}
                            containerStyle={{
                              marginTop: "42px",
                            }}
                            minheight={false}
                          />
                        </BsCol>
                      </BsRow>
                      <BsRow class="mt-5">
                        <BsCol class="col-md-6 text-end">
                          <Button
                            type="button"
                            color="error"
                            size="large"
                            sx={{ width: 80 }}
                            className="roundedButton ml-md-2"
                            variant="outlined"
                            disabled={loadingQueryState}
                            onClick={clearForm}
                          >
                            Limpiar
                          </Button>
                        </BsCol>
                        <BsCol class="col-md-6 text-start">
                          <SaveAction
                            title="Guardar"
                            color="secondary"
                            size="large"
                            className="roundedButton ml-md-2"
                            variant="contained"
                            disabled={loadingQueryState}
                            messageType="add"
                            mutationLoading={loadingQueryState}
                            success={successQueryState}
                            actionClose={onCloseSnack}
                          />
                        </BsCol>
                      </BsRow>
                    </form>
                  </BsCol>
                </BsRow>
                {errorState.error && (
                  <Snackbar
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    open={errorState.error}
                    onClose={() =>
                      setErrorState({
                        message: "",
                        error: false,
                      })
                    }
                    autoHideDuration={3000}
                    key="error-snackbar2"
                  >
                    <Alert
                      onClose={handleCloseSnackBarsComponent}
                      severity="error"
                      variant="filled"
                      sx={{ width: "100%" }}
                    >
                      {errorState.message}
                    </Alert>
                  </Snackbar>
                )}
              </CardContent>
            </Card>
          </BsCol>
        </BsRow>
      </BsContainer>
    </div>
  );
};
export default PhAdd;
